import React from 'react';
import EntityActionRegistry, { EntityAction } from './EntityActionRegistry';
import { GenericEntityUpsertDialog, WorkflowStepEditDialog } from '../components/dialogs';
import { WorkflowDesigner } from '../components/workflows/WorkflowDesigner';

interface ActionHandlerProps {
    entityType: string;
    actionId: string;
    entityId?: string;
    onClose?: () => void;
    onSuccess?: (result: any) => void;
}

class EntityActionHandler {
    private static instance: EntityActionHandler;
    private actionComponents: Map<string, React.ComponentType<any>> = new Map();

    public static getInstance(): EntityActionHandler {
        if (!EntityActionHandler.instance) {
            EntityActionHandler.instance = new EntityActionHandler();
            EntityActionHandler.instance.initializeDefaultActions();
        }
        return EntityActionHandler.instance;
    }

    private initializeDefaultActions(): void {
        // Register Set creation action (uses GenericEntityUpsertDialog with SetFormLayout)
        this.registerActionComponent('set-create', GenericEntityUpsertDialog);

        // Register PolicyRule creation action
        this.registerActionComponent('policyrule-create', GenericEntityUpsertDialog);

        // Register Workflow creation action
        this.registerActionComponent('workflow-create', WorkflowDesigner);

        // Register WorkflowActivity creation action
        this.registerActionComponent('workflowactivity-create', WorkflowStepEditDialog);

        // Register other default actions here as needed
    }

    /**
     * Register a component for a specific action
     */
    public registerActionComponent(actionKey: string, component: React.ComponentType<any>): void {
        this.actionComponents.set(actionKey, component);
    }

    /**
     * Get the component for a specific action
     */
    public getActionComponent(actionKey: string): React.ComponentType<any> | null {
        return this.actionComponents.get(actionKey) || null;
    }

    /**
     * Execute an action and return the appropriate component
     */
    public executeAction(props: ActionHandlerProps): React.ReactElement | null {
        const { entityType, actionId, entityId, onClose, onSuccess } = props;

        const actionRegistry = EntityActionRegistry.getInstance();
        const action = actionRegistry.getAction(entityType, actionId);

        if (!action) {
            console.warn(`No action found for entity type: ${entityType}, action: ${actionId}`);
            return null;
        }

        // Create action key for component lookup
        const actionKey = `${entityType.toLowerCase()}-${actionId}`;
        const ActionComponent = this.getActionComponent(actionKey) || action.component;

        if (!ActionComponent) {
            console.warn(`No component found for action: ${actionKey}`);
            return null;
        }

        // Render the action component with appropriate props
        return React.createElement(ActionComponent, {
            open: true,
            onClose: onClose,
            onSuccess: onSuccess,
            entityId: entityId,
            entityType: entityType,
            actionId: actionId
        });
    }

    /**
     * Check if an action is available for an entity type
     */
    public isActionAvailable(entityType: string, actionId: string): boolean {
        const actionRegistry = EntityActionRegistry.getInstance();
        const action = actionRegistry.getAction(entityType, actionId);
        return action !== null;
    }

    /**
     * Get all available actions for an entity type
     */
    public getAvailableActions(entityType: string): EntityAction[] {
        const actionRegistry = EntityActionRegistry.getInstance();
        return actionRegistry.getActions(entityType);
    }
}

export default EntityActionHandler;
